import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import Map from '@arcgis/core/Map';
import type { ClickEvent } from '@arcgis/core/views/input/types';
import type { NavigationProperties } from '@arcgis/core/views/navigation/Navigation';
import type { GraphicHit } from '@arcgis/core/views/types';
import '@arcgis/map-components/components/arcgis-map';
import { debounceTime, filter, fromEvent, merge, tap } from 'rxjs';
import { areaLayer } from '../../layers/area.layer';
import { outagesPointLayer, outagesPolygonLayer } from '../../layers/outages.layer';
import { tileLayer } from '../../layers/tile.layer';
import { MapService } from '../../map.service';
import { DeepPartial } from '../../utils/deep-partial';
import { generateClusterConfig } from '../../utils/generate-cluster-config';
import { isMac } from '../../utils/platform';
import { InfoContainerStore } from '../info-container/info-container.store';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MapComponent implements OnInit {
  private readonly infoContainerStore = inject(InfoContainerStore);
  private readonly mapService = inject(MapService);
  private readonly mapElement = viewChild.required<ElementRef<HTMLArcgisMapElement>>('map');

  protected readonly isHoldingZoomKey = signal(false);
  protected readonly isScrolling = signal(false);
  protected readonly isMac = isMac();

  protected readonly mapConfig = new Map({
    layers: [tileLayer, areaLayer, outagesPointLayer, outagesPolygonLayer],
  });

  protected readonly navigation = computed<DeepPartial<NavigationProperties>>(() => ({
    actionMap: {
      dragSecondary: 'none',
      mouseWheel: this.isHoldingZoomKey() ? 'zoom' : 'none',
    },
  }));

  private readonly animationOptions = {
    animationMode: 'always',
    maxDuration: 400,
  } as const;

  constructor() {
    effect(() => {
      this.mapService.setMap(this.mapElement());
    });
  }

  ngOnInit() {
    this.initOutagesLayerHandling();
    this.initKeyboardListeners();
    this.initScrollListener();
  }

  setLoadingStateOnViewReady() {
    this.mapService.setIsLoading(false);
  }

  async selectOutageAtClick(event: CustomEvent<ClickEvent>) {
    try {
      const hitTestResult = await this.mapElement().nativeElement.hitTest(event.detail, {
        include: [outagesPointLayer, outagesPolygonLayer],
      });
      const graphicHits = hitTestResult.results.filter(
        (result): result is GraphicHit => result.type === 'graphic',
      );

      if (!graphicHits.length) {
        this.infoContainerStore.resetAccordion();
        return;
      }

      const hit = graphicHits.at(0);
      if (!hit) return;

      const target = hit.graphic.geometry;
      if (!target) return;

      //aggregated outages, so zoom in more to see individual outages
      if (this.isAggregatedPoint(hit)) {
        const currentZoom = this.mapElement().nativeElement.view.zoom;
        await this.mapElement().nativeElement.goTo(
          { target, zoom: currentZoom + 4 },
          this.animationOptions,
        );
        return;
      }

      //individual outage, so select it
      const outageId = hit.graphic.getObjectId();
      if (!outageId) return;

      this.infoContainerStore.setSelectedOutage(+outageId);
      this.infoContainerStore.setView('outage');
      this.infoContainerStore.setIsOpen(true);

      await this.mapElement().nativeElement.goTo(target, this.animationOptions);
    } catch (e) {
      console.error('Error during map click:', e);
    }
  }

  private isAggregatedPoint(graphicHit: GraphicHit) {
    if (graphicHit.graphic.isAggregate) return true;

    return graphicHit.graphic.geometry?.type !== 'polygon';
  }

  private initKeyboardListeners() {
    const zoomKey = this.isMac ? 'Meta' : 'Control';

    const keydownEvents = fromEvent<KeyboardEvent>(document, 'keydown').pipe(
      filter(e => e.key === zoomKey),
    );

    const keyupEvents = fromEvent<KeyboardEvent>(document, 'keyup').pipe(
      filter(e => e.key === zoomKey),
    );

    keydownEvents.subscribe(() => {
      this.isHoldingZoomKey.set(true);
    });

    keyupEvents.subscribe(() => {
      this.isHoldingZoomKey.set(false);
    });

    merge(keydownEvents, keyupEvents)
      .pipe(
        filter(() => this.isScrolling()),
        tap(() => {
          this.isScrolling.set(false);
        }),
      )
      .subscribe();
  }

  private initScrollListener() {
    fromEvent<WheelEvent>(this.mapElement().nativeElement, 'wheel')
      .pipe(
        filter(() => !this.isHoldingZoomKey()),
        tap(() => {
          this.isScrolling.set(true);
        }),
        debounceTime(2000),
        tap(() => {
          this.isScrolling.set(false);
        }),
      )
      .subscribe();
  }

  private initOutagesLayerHandling() {
    outagesPointLayer
      .when()
      .then(generateClusterConfig)
      .then(featureReduction => {
        outagesPointLayer.featureReduction = featureReduction;
      })
      .catch((error: unknown) => {
        console.error(error);
      });

    outagesPointLayer.on('refresh', event => {
      if (event.dataChanged) {
        this.mapService.map()?.nativeElement.map?.remove(outagesPointLayer);
        this.mapService.map()?.nativeElement.map?.add(outagesPointLayer);

        generateClusterConfig()
          .then(featureReduction => {
            outagesPointLayer.featureReduction = featureReduction;
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    });
  }
}
